import { dataThemeAttributeName } from 'src/app/constants';
import { CoBrandId } from 'src/WebApiController';
import { DefaultTheme } from 'styled-components';

// Cache for loaded themes to avoid re-importing
const themeCache = new Map<string, string>();
const legacyThemeCache = new Map<string, DefaultTheme>();

// Dynamic theme loaders
const loadTheme = async (themeName: string): Promise<string> => {
  if (themeCache.has(themeName)) {
    return themeCache.get(themeName)!;
  }

  let themeClass: string;

  switch (themeName) {
    case 'dark':
      themeClass = (await import('./darkTheme.css')).darkTheme;
      break;
    case 'purple':
      themeClass = (await import('./purpleTheme.css')).purpleTheme;
      break;
    case 'reachPro':
      themeClass = (await import('./reachProTheme.css')).reachProTheme;
      break;
    case 'reachProDark':
      themeClass = (await import('./reachProDarkTheme.css')).reachProDarkTheme;
      break;
    case 'ticketUtil':
      themeClass = (await import('./ticketUtilTheme.css')).ticketUtilTheme;
      break;
    default:
      throw new Error(`Unknown theme: ${themeName}`);
  }

  themeCache.set(themeName, themeClass);
  return themeClass;
};

export const loadLegacyTheme = async (
  themeName: string
): Promise<DefaultTheme> => {
  if (legacyThemeCache.has(`legacy-${themeName}`)) {
    return legacyThemeCache.get(`legacy-${themeName}`)!;
  }

  let legacyTheme: DefaultTheme;

  switch (themeName) {
    case 'dark':
      legacyTheme = (await import('./darkLegacyTheme')).DarkLegacyTheme;
      break;
    case 'purple':
      legacyTheme = (await import('./purpleLegacyTheme')).PurpleLegacyTheme;
      break;
    case 'reachPro':
      legacyTheme = (await import('./reachProLegacyTheme')).ReachProLegacyTheme;
      break;
    case 'reachProDark':
      legacyTheme = (await import('./reachProDarkLegacyTheme'))
        .ReachProDarkLegacyTheme;
      break;
    case 'ticketUtil':
      legacyTheme = (await import('./ticketUtilLegacyTheme'))
        .TicketUtilLegacyTheme;
      break;
    default:
      throw new Error(`Unknown legacy theme: ${themeName}`);
  }

  legacyThemeCache.set(`legacy-${themeName}`, legacyTheme);
  return legacyTheme;
};

export const getLegacyThemeForCoBrand = async (
  cobrandId: CoBrandId | undefined,
  isDarkMode: boolean
) => {
  const themeName = getThemeNameForCoBrand(cobrandId, isDarkMode);
  return await loadLegacyTheme(themeName);
};

// Helper function to determine theme name
const getThemeNameForCoBrand = (
  cobrandId: CoBrandId | undefined,
  isDarkMode: boolean
): string => {
  if (cobrandId === CoBrandId.TicketUtil) {
    return 'ticketUtil';
  }

  if (cobrandId === CoBrandId.ReachPro) {
    return isDarkMode ? 'reachProDark' : 'reachPro';
  }

  // Default to StubHub
  return isDarkMode ? 'dark' : 'purple';
};

// Keep track of currently applied theme classes for cleanup
const appliedThemeClasses = new Set<string>();

export const adjustBodyThemeClasses = async (
  cobrandId: CoBrandId | undefined,
  isDarkMode: boolean
) => {
  // Set theme attribute
  document.body.setAttribute(
    dataThemeAttributeName,
    isDarkMode ? 'dark' : 'light'
  );

  // Remove all previously applied theme classes
  appliedThemeClasses.forEach((className) => {
    document.body.classList.remove(className);
  });
  appliedThemeClasses.clear();

  // Determine and apply new theme
  const themeName = getThemeNameForCoBrand(cobrandId, isDarkMode);

  try {
    const themeClass = await loadTheme(themeName);
    document.body.classList.add(themeClass);
    appliedThemeClasses.add(themeClass);
  } catch (error) {
    console.error(`Failed to load theme: ${themeName}`, error);

    // Fallback to purple theme
    try {
      const fallbackTheme = await loadTheme('purple');
      document.body.classList.add(fallbackTheme);
      appliedThemeClasses.add(fallbackTheme);
    } catch (fallbackError) {
      console.error('Failed to load fallback theme', fallbackError);
    }
  }
};

// Preload commonly used themes (optional optimization)
export const preloadThemes = async (cobrandId?: CoBrandId) => {
  const themesToPreload: string[] = [];

  switch (cobrandId) {
    case CoBrandId.TicketUtil:
      themesToPreload.push('ticketUtil');
      break;
    case CoBrandId.ReachPro:
      themesToPreload.push('reachPro', 'reachProDark');
      break;
    default:
      themesToPreload.push('purple', 'dark');
      break;
  }

  // Preload themes in parallel
  await Promise.allSettled(themesToPreload.map((theme) => loadTheme(theme)));
};

// Utility function to clear theme cache (useful for testing)
export const clearThemeCache = () => {
  themeCache.clear();
};

// Export for backward compatibility (but mark as deprecated)
/** @deprecated Use adjustBodyThemeClasses instead */
export const setTheme = adjustBodyThemeClasses;
